import { RotateCcwIcon } from "lucide-react";
import {
  Outlet,
  createFileRoute,
  redirect,
  useCanGoBack,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useSettingsRestore } from "../components/settings/SettingsPanels";
import { SettingsBreadcrumb } from "../components/settings/SettingsBreadcrumb";
import { SettingsSearchTargetProvider } from "../components/settings/settingsLayout";
import { Button } from "../components/ui/button";
import { SidebarInset } from "../components/ui/sidebar";
import { isElectron } from "../env";
import { readLocalApi } from "../localApi";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import { useEnvironment } from "../state/environments";
import { isClientLocalSettingsPath } from "../components/settings/feedbackSettings.logic";
import { useEnvironmentSettingsScope } from "../state/environmentSettingsScope";
import {
  SettingsScopeProvider,
  useSettingsScope,
} from "../components/settings/SettingsScopeContext";
import { selectProjectGroupingSettings } from "../logicalProject";
import { buildSidebarProjectSnapshots } from "../sidebarProjectGrouping";
import { useClientSettings } from "../hooks/useSettings";
import { useProjects } from "../state/entities";
import { useEnvironments, usePrimaryEnvironmentId } from "../state/environments";
import { validateSettingsScopeSearch } from "../components/settings/settingsScope";
import {
  WorkspaceShellHeader,
  WorkspaceShellMain,
  WorkspaceShellStatus,
} from "../components/WorkspaceShellPrimitives";

function RestoreDefaultsButton({ onRestored }: { onRestored: () => void }) {
  const { changedSettingLabels, restoreDefaults } = useSettingsRestore(onRestored);

  return (
    <Button
      size="xs"
      variant="ghost"
      disabled={changedSettingLabels.length === 0}
      onClick={() => void restoreDefaults()}
    >
      <RotateCcwIcon className="mx-1 size-3.5" />
      Restore device defaults
    </Button>
  );
}

function SettingsContentLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const [restoreSignal, setRestoreSignal] = useState(0);
  const settingsScope = useEnvironmentSettingsScope();
  const settingsEnvironment = useEnvironment(settingsScope?.environmentId ?? null);
  const environmentReady = settingsEnvironment?.connection.phase === "ready";
  const clientLocalSettings = isClientLocalSettingsPath(
    readLocalApi()?.connect !== undefined,
    location.pathname,
  );
  const showRestoreDefaults = environmentReady && location.pathname === "/settings/general";
  const { search: scopeSearch, selectScope } = useSettingsScope();
  const projects = useProjects();
  const { environments } = useEnvironments();
  const groupingSettings = useClientSettings(selectProjectGroupingSettings);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const scopeGroups = useMemo(
    () =>
      buildSidebarProjectSnapshots({
        projects,
        settings: groupingSettings,
        resolveEnvironmentLabel: (environmentId) =>
          environments.find((candidate) => candidate.environmentId === environmentId)?.label ??
          null,
        primaryEnvironmentId,
      }),
    [environments, groupingSettings, primaryEnvironmentId, projects],
  );
  // Device-local pages never reach a server, so a scope there would be a
  // selection with nothing to apply to.
  const scopeBreadcrumb = clientLocalSettings
    ? undefined
    : { value: scopeSearch, groups: scopeGroups, environments, onChange: selectScope };
  const searchTargetId = location.hash.replace(/^#/, "") || null;
  const handleRestored = () => setRestoreSignal((value) => value + 1);
  const clearSearchTarget = useCallback(() => {
    // Only the hash goes: the search names the page the target is on, such as an Environment's.
    void navigate({
      hash: "",
      search: true,
      replace: true,
      resetScroll: false,
      hashScrollIntoView: false,
    });
  }, [navigate]);
  const navigateBackWithinApp = useCallback(() => {
    if (canGoBack) {
      window.history.back();
      return;
    }
    void navigate({ to: "/" });
  }, [canGoBack, navigate]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        navigateBackWithinApp();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [navigateBackWithinApp]);

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
        {!isElectron && (
          <WorkspaceShellHeader
            className={cn(
              "border-b border-border px-3 py-2 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none sm:px-5",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            <div className="flex min-h-7 w-full items-center gap-3 sm:min-h-6">
              <SettingsBreadcrumb pathname={location.pathname} scope={scopeBreadcrumb} />
              {showRestoreDefaults ? (
                <div className="ms-auto flex shrink-0 items-center">
                  <RestoreDefaultsButton onRestored={handleRestored} />
                </div>
              ) : null}
            </div>
          </WorkspaceShellHeader>
        )}

        {isElectron && (
          <WorkspaceShellHeader
            className={cn(
              "drag-region flex h-[52px] shrink-0 items-center border-b border-border px-5 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none wco:h-[env(titlebar-area-height)] wco:pr-[calc(100vw-env(titlebar-area-width)-env(titlebar-area-x)+1em)]",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            <div className="no-drag flex w-full min-w-0 items-center gap-3">
              <SettingsBreadcrumb pathname={location.pathname} scope={scopeBreadcrumb} />
              {showRestoreDefaults ? (
                <div className="ms-auto flex shrink-0 items-center">
                  <RestoreDefaultsButton onRestored={handleRestored} />
                </div>
              ) : null}
            </div>
          </WorkspaceShellHeader>
        )}

        <WorkspaceShellMain key={restoreSignal} className="min-h-0 flex flex-1 flex-col">
          <SettingsSearchTargetProvider
            targetId={searchTargetId}
            onTargetHandled={clearSearchTarget}
          >
            {environmentReady || clientLocalSettings ? (
              <Outlet />
            ) : (
              <WorkspaceShellStatus
                kind="error"
                className="flex flex-1 items-center justify-center p-8 text-center"
              >
                <div className="max-w-md space-y-2">
                  <h2 className="text-base font-semibold">Environment unavailable</h2>
                  <p className="text-sm text-muted-foreground">
                    Environment-backed settings and actions are available after synchronization is
                    ready.
                  </p>
                </div>
              </WorkspaceShellStatus>
            )}
          </SettingsSearchTargetProvider>
        </WorkspaceShellMain>
      </div>
    </SidebarInset>
  );
}

function SettingsRouteLayout() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  return (
    <SettingsScopeProvider
      search={search}
      onChange={(next) => {
        // The selection replaces the search outright, so clearing an axis is
        // its absence rather than a leftover from the previous one.
        void navigate({
          to: pathname,
          search: () => next,
          hash: "",
          resetScroll: false,
        });
      }}
    >
      <SettingsContentLayout />
    </SettingsScopeProvider>
  );
}

export const Route = createFileRoute("/settings")({
  validateSearch: validateSettingsScopeSearch,
  beforeLoad: async ({ location }) => {
    if (location.pathname === "/settings") {
      throw redirect({ to: "/settings/general", replace: true });
    }
  },
  component: SettingsRouteLayout,
});
